import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Banknote, CreditCard, User, Hash, Printer } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import { render, printDocument } from '../utils/print.js';
import { PLANTILLAS_DEFAULT } from '../data/plantillasDefault.js';

const fmtMonto = (v) =>
  Number(v).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 });

const fmtFecha = (v) =>
  new Date(v).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });

function ProveedorTypeahead({ value, onChange }) {
  const [q, setQ]          = useState('');
  const [opciones, setOpc] = useState([]);
  const [abierto, setAb]   = useState(false);
  const ref                 = useRef(null);
  const timerRef            = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setAb(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const buscar = (txt) => {
    setQ(txt);
    if (!value) onChange(null);
    clearTimeout(timerRef.current);
    if (txt.length < 1) { setOpc([]); setAb(false); return; }
    timerRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/proveedores/index.php', { params: { search: txt, limit: 8 } });
        setOpc(res.data);
        setAb(true);
      } catch {}
    }, 300);
  };

  const seleccionar = (p) => {
    onChange(p);
    setQ(p.razon_social);
    setAb(false);
  };

  const limpiar = () => { onChange(null); setQ(''); setOpc([]); setAb(false); };

  return (
    <div ref={ref} className="relative">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="input pl-8"
            placeholder="Buscar proveedor..."
            value={q}
            onChange={(e) => buscar(e.target.value)}
            onFocus={() => q.length > 0 && setAb(true)}
          />
        </div>
        {value && (
          <button type="button" onClick={limpiar} className="btn-secondary text-xs px-3">Limpiar</button>
        )}
      </div>
      {abierto && opciones.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-auto">
          {opciones.map(p => (
            <li key={p.id}>
              <button
                type="button"
                className="w-full text-left px-4 py-2.5 hover:bg-slate-50 text-sm"
                onClick={() => seleccionar(p)}
              >
                <span className="font-medium text-slate-900">{p.razon_social}</span>
                {p.cuit && <span className="ml-2 text-xs text-slate-400 font-mono">{p.cuit}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const emptyForm = {
  proveedor: null, compra_id: '', monto: '', forma_pago_id: '', fecha: new Date().toISOString().slice(0, 10), notas: '',
};

function PagoForm({ form, onChange, formasPago }) {
  const set   = (f) => (v) => onChange({ ...form, [f]: v });
  const setEv = (f) => (e) => onChange({ ...form, [f]: e.target.value });

  return (
    <div className="space-y-4">
      <div>
        <label>Proveedor</label>
        <ProveedorTypeahead value={form.proveedor} onChange={set('proveedor')} />
        <p className="text-xs text-slate-400 mt-1">Opcional — dejá vacío para pagos genéricos.</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>Monto *</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
            <input
              className="input pl-7"
              type="number"
              min="0"
              step="0.01"
              placeholder="0,00"
              value={form.monto}
              onChange={setEv('monto')}
            />
          </div>
        </div>
        <div>
          <label>Fecha *</label>
          <input className="input" type="date" value={form.fecha} onChange={setEv('fecha')} />
        </div>
      </div>

      <div>
        <label>Forma de pago</label>
        <select className="input" value={form.forma_pago_id} onChange={setEv('forma_pago_id')}>
          <option value="">— Sin especificar —</option>
          {formasPago.map(fp => <option key={fp.id} value={fp.id}>{fp.nombre}</option>)}
        </select>
      </div>

      <div>
        <label className="flex items-center gap-1.5">
          <Hash size={13} className="text-slate-400" /> N.° de compra vinculada
        </label>
        <input
          className="input font-mono"
          placeholder="Ej: 42"
          value={form.compra_id}
          onChange={setEv('compra_id')}
        />
        <p className="text-xs text-slate-400 mt-1">Número interno de la factura de compra (opcional).</p>
      </div>

      <div>
        <label>Notas</label>
        <textarea className="input resize-none" rows={2} placeholder="Observaciones..." value={form.notas} onChange={setEv('notas')} />
      </div>
    </div>
  );
}

export default function Pagos() {
  const { can } = useAuth();
  const [lista,      setLista]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [formasPago, setFormasPago] = useState([]);

  const [filtros, setFiltros] = useState({ fecha_desde: '', fecha_hasta: '' });

  const [modalOpen, setModalOpen] = useState(false);
  const [form,      setForm]      = useState(emptyForm);
  const [saving,    setSaving]    = useState(false);

  const [confirmId, setConfirmId] = useState(null);
  const [deleting,  setDeleting]  = useState(false);

  const puedeGestionar = can('pagos.gestionar');

  useEffect(() => {
    api.get('/formas-pago/index.php').then(r => setFormasPago(r.data)).catch(() => {});
  }, []);

  const fetchPagos = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      const res = await api.get('/pagos/index.php', { params });
      setLista(res.data);
    } catch { toast.error('Error al cargar pagos'); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { fetchPagos(); }, [fetchPagos]);

  const openCreate = () => { setForm(emptyForm); setModalOpen(true); };

  const handleSave = async () => {
    if (!form.monto || parseFloat(form.monto) <= 0) { toast.error('El monto debe ser mayor a cero'); return; }
    setSaving(true);
    try {
      await api.post('/pagos/index.php', {
        proveedor_id:  form.proveedor?.id  || null,
        compra_id:     form.compra_id      ? parseInt(form.compra_id) : null,
        monto:         parseFloat(form.monto),
        forma_pago_id: form.forma_pago_id  ? parseInt(form.forma_pago_id) : null,
        fecha:         form.fecha          || null,
        notas:         form.notas,
      });
      toast.success('Pago registrado');
      setModalOpen(false);
      fetchPagos();
    } catch (err) {
      toast.error(err?.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/pagos/index.php?id=${confirmId}`);
      toast.success('Pago eliminado');
      setConfirmId(null);
      fetchPagos();
    } catch (err) {
      toast.error(err?.message || 'Error al eliminar');
    } finally { setDeleting(false); }
  };

  const totalFiltrado = lista.reduce((s, p) => s + p.monto, 0);

  const handlePrint = async (p) => {
    let html = PLANTILLAS_DEFAULT.pago.html;
    try {
      const res = await api.get('/plantillas/index.php?tipo=pago');
      if (res.data?.html) html = res.data.html;
    } catch { /* usa default */ }
    const data = {
      id: p.id,
      fecha: fmtFecha(p.fecha),
      proveedor: p.proveedor || '—',
      forma_pago: p.forma_pago || '—',
      referencia: p.referencia || '—',
      monto_fmt: fmtMonto(p.monto),
      usuario: p.usuario || '—',
      notas: p.notas || '—',
    };
    printDocument(render(html, data));
  };

  return (
    <div className="space-y-5">
      <div className="page-header">
        <h1 className="page-title">Pagos a proveedores</h1>
        {puedeGestionar && (
          <button className="btn-primary" onClick={openCreate}>
            <Plus size={18} /> Nuevo pago
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="card flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Desde</label>
          <input
            className="input"
            type="date"
            value={filtros.fecha_desde}
            onChange={(e) => setFiltros(f => ({ ...f, fecha_desde: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Hasta</label>
          <input
            className="input"
            type="date"
            value={filtros.fecha_hasta}
            onChange={(e) => setFiltros(f => ({ ...f, fecha_hasta: e.target.value }))}
          />
        </div>
        {(filtros.fecha_desde || filtros.fecha_hasta) && (
          <button
            className="btn-secondary text-sm"
            onClick={() => setFiltros({ fecha_desde: '', fecha_hasta: '' })}
          >
            Limpiar filtros
          </button>
        )}
        {lista.length > 0 && (
          <div className="ml-auto text-right">
            <p className="text-xs text-slate-400">Total en vista</p>
            <p className="text-lg font-semibold text-slate-900">{fmtMonto(totalFiltrado)}</p>
          </div>
        )}
      </div>

      {/* Tabla */}
      <div className="card !p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : lista.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-300">
            <Banknote size={48} strokeWidth={1.5} />
            <p className="text-sm mt-3 text-slate-400">No hay pagos en el período seleccionado</p>
            {puedeGestionar && (
              <button className="btn-primary mt-4" onClick={openCreate}><Plus size={16} /> Registrar primero</button>
            )}
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Proveedor</th>
                  <th>Compra</th>
                  <th>Forma de pago</th>
                  <th className="text-right">Monto</th>
                  <th>Notas</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lista.map(p => (
                  <tr key={p.id}>
                    <td className="text-slate-500 text-sm whitespace-nowrap">{fmtFecha(p.fecha)}</td>
                    <td>
                      {p.proveedor
                        ? <span className="font-medium text-slate-900">{p.proveedor}</span>
                        : <span className="text-slate-300 text-xs">Sin proveedor</span>}
                    </td>
                    <td>
                      {p.compra_numero
                        ? <span className="badge-neutral font-mono">#{String(p.compra_numero).padStart(4, '0')}</span>
                        : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td>
                      {p.forma_pago
                        ? <span className="flex items-center gap-1 text-sm text-slate-600"><CreditCard size={13} />{p.forma_pago}</span>
                        : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="text-right font-semibold text-slate-900">{fmtMonto(p.monto)}</td>
                    <td className="text-slate-400 text-sm max-w-[180px] truncate">{p.notas || '—'}</td>
                    <td>
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={() => handlePrint(p)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                          title="Imprimir comprobante"
                        >
                          <Printer size={14} />
                        </button>
                        {puedeGestionar && (
                          <button
                            onClick={() => setConfirmId(p.id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Nuevo pago"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Registrar pago'}
            </button>
          </>
        }
      >
        <PagoForm form={form} onChange={setForm} formasPago={formasPago} />
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Eliminar pago"
        message="¿Seguro que querés eliminar este pago? Esta acción no se puede deshacer."
      />
    </div>
  );
}
